import { Controller, Control, FieldPath, FieldValues } from 'react-hook-form';
import { Stack, styled, Box, FormControlLabel } from '@mui/material';
import { theme } from '~/shared/styles/theme';

interface RadioOption {
  value: string;
  label: string;
}

interface Props<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  options: RadioOption[];
}

const InvitationRadioGroup = <T extends FieldValues>({
  name,
  control,
  options,
}: Props<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <WhiteContainer>
          <RadioContainer data-testid="radio-button-group" role="radiogroup">
            {options.map((option) => (
              <Option
                key={option.value}
                onClick={() => field.onChange(option.value)}
                data-testid={`radio-card-${option.value}`}
              >
                <StyledFormControlLabel
                  control={
                    <IconWrapper data-testid={`radio-button-${option.value}`}>
                      <HiddenRadioInput
                        type="radio"
                        name={name}
                        value={option.value}
                        checked={field.value === option.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        aria-label={option.label}
                      />
                      {field.value === option.value ? (
                        <CheckmarkFilled />
                      ) : (
                        <CheckmarkEmpty />
                      )}
                    </IconWrapper>
                  }
                  label={option.label}
                />
              </Option>
            ))}
          </RadioContainer>
        </WhiteContainer>
      )}
    />
  );
};

export default InvitationRadioGroup;

const CheckmarkEmpty = () => (
  <svg
    width="31"
    height="31"
    viewBox="0 0 31 31"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="15.5"
      cy="15.5"
      r="14.5"
      stroke={theme.palette.custom.green}
      strokeWidth="2"
    />
  </svg>
);

const CheckmarkFilled = () => (
  <svg
    width="31"
    height="31"
    viewBox="0 0 31 31"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="15.5" cy="15.5" r="15.5" fill={theme.palette.custom.green} />
    <path
      d="M22 10 12 21 8 17"
      stroke="#fff"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const WhiteContainer = styled(Stack)({
  width: '100%',
  backgroundColor: theme.palette.common.white,
  borderRadius: '8px',
  padding: theme.spacing(6),
  marginTop: '24px',
  marginBottom: '35px',
});

const RadioContainer = styled(Stack)({
  width: '100%',
  flexDirection: 'row',
  gap: theme.spacing(2),
});

const Option = styled(Box)({
  cursor: 'pointer',
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
});

const IconWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
});

const HiddenRadioInput = styled('input')({
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
});

const StyledFormControlLabel = styled(FormControlLabel)({
  margin: 0,
  gap: theme.spacing(1),
  padding: theme.spacing(4, 20),
  width: '100%',
  '& .MuiFormControlLabel-label': {
    fontSize: '16px',
    color: theme.palette.custom.darkBrown,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
});
